import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import {components, type MenuListProps} from 'react-select'
import {useReactFlow, type Node} from 'reactflow'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import {getalltagsdata, CreateData} from '../../../../Contacts/Tags/TagSlice/TagsSlices'
import TagPopupModal from '../../../../Contacts/AllContactsList/Modal/TagPopupModal'
import {type DataNode, type MetaDataWithAddTag, type TagOption} from '../../FlowTypes'
import {NodeType} from '../../../../../../Utils/Constant/FlowConstants'

type AddTagSidebarNode = Node<DataNode<MetaDataWithAddTag>, NodeType>

interface TagsState {
  tags?: {
    allTags?: {
      data?: RawTag[]
    }
  }
}

interface RootState {
  tags?: TagsState['tags']
}

interface RawTag {
  _id: string
  name: string
  isHidden?: boolean
  type?: string | null
}

const AddTagSidebar: React.FC<{node: AddTagSidebarNode}> = ({node}) => {
  const {t} = useI18n()
  const dispatch = useDispatch()
  const {setNodes} = useReactFlow()
  const [openTagPopup, setOpenTagPopup] = useState(false)

  const initialTags: TagOption[] = Array.isArray(node.data?.metaData?.tags)
    ? (node.data?.metaData?.tags as TagOption[])
    : []

  const [tagOptions, setTagOptions] = useState<TagOption[]>([])
  const [selectedTags, setSelectedTags] = useState<TagOption[]>(initialTags)

  useEffect(() => {
    dispatch(getalltagsdata())
  }, [dispatch])

  const tagslist = useSelector((state: unknown) => {
    const root = state as RootState
    return root.tags?.allTags?.data ?? []
  })

  useEffect(() => {
    const filteredTags = tagslist.filter((val) => {
      if (val.type) {
        return val.isHidden === false && val.type !== 'list'
      }
      return val.isHidden === false
    })
    const listData: TagOption[] = filteredTags.map((val) => ({
      value: val._id,
      label: val.name,
    }))
    setTagOptions(listData)
  }, [tagslist])

  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? {
              ...n,
              data: {
                ...n.data,
                metaData: {
                  ...(n.data?.metaData ?? {}),
                  tags: selectedTags,
                },
              },
            }
          : n
      )
    )
  }, [node.id, selectedTags, setNodes])

  const handleTagsChange = (value: readonly TagOption[] | null) => {
    setSelectedTags(value ? [...value] : [])
  }

  const handleCreateTag: (inputValue: string) => void = (inputValue) => {
    const newTag: TagOption = {
      value: inputValue,
      label: inputValue,
    }
    setSelectedTags((prev) => [...prev, newTag])
    dispatch(
      CreateData({
        name: inputValue,
      })
    )
  }

  const MenuList = (props: MenuListProps<TagOption, true>) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div
          style={{
            width: '100%',
            display: 'flex',
            gap: '6px',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            borderTop: '1px solid #F4F5F7',
            paddingTop: '10px',
          }}
          onClick={() => {
            setOpenTagPopup(true)
          }}
        >
          <i
            style={{
              color: '#7950F2',
              fontWeight: 'bold',
            }}
            className='bi bi-plus-lg'
          ></i>
          <p
            style={{
              color: '#7950F2',
              margin: 0,
              fontWeight: 600,
            }}
          >
            {t('COMMON.ACTIONS.CREATE')}
          </p>
        </div>
      </components.MenuList>
    )
  }

  const AddTagPopup = useMemo(
    () => (
      <TagPopupModal
        selectedTag={selectedTags}
        setSelectedTag={setSelectedTags}
        show={openTagPopup}
        handleClose={() => setOpenTagPopup(false)}
      />
    ),
    [openTagPopup, selectedTags]
  )

  return (
    <div className='p-4'>
      <div className='d-flex align-items-center gap-3 mb-4'>
        <div
          className='automation-icon-container'
          style={{
            backgroundColor: '#E0F0FF',
          }}
        >
          <i className='bi bi-tag' />
        </div>
        <h3 className='mb-0 fw-semibold text-truncate' style={{maxWidth: '220px'}}>
          {t('FLOW.NODE.ADD_TAG')}
        </h3>
      </div>

      <div className='trigger-sidebar-dropdown'>
        <label className='form-label fw-bold'>{t('FLOW.SIDEBAR.SELECT_TAG')}</label>
        <CreatableSelect<TagOption, true>
          value={selectedTags}
          components={{MenuList}}
          className='react-tags-selectbox common-select z-1000'
          options={tagOptions}
          onChange={handleTagsChange}
          onCreateOption={handleCreateTag}
          isClearable
          isMulti
          styles={{
            control: (base) => ({
              ...base,
              backgroundColor: 'var(--bs-gray-200) !important',
              borderColor: base.borderColor,
              boxShadow: base.boxShadow,
              minHeight: 32,
            }),
            valueContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
            indicatorsContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
          }}
          menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
          closeMenuOnScroll={false}
          menuShouldBlockScroll
        />
        {AddTagPopup}
      </div>
    </div>
  )
}

export default AddTagSidebar

